import React, { useEffect, useMemo, useState, useContext } from 'react';
import { makeStyles } from '@fluentui/react-components';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { flatten, noop, every, isEmpty } from 'underscore';
import { IndustryTargetSetting } from '@bingads-webui-campaign/audience-criterion-setting';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { AudienceTargetInfo } from '@bingads-webui-campaign/audience-target-info';
import { criterionHelper } from '@bingads-webui-campaign/audience-campaign-shared';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getIndustryTargetStyles } from './styles/industry-target-styles';
import { withBidAdjustment } from './with-bid-adjustment';

export const formatIndustry = ({ industry, i18n }) => {
  if (!industry || !industry.length) {
    return i18n.getString(_TL_('Target Industries'));
  }

  if (industry.length === 1) {
    return i18n.getString(_TL_('Targeted: 1 industry'));
  }

  return i18n.getString(_TL_('Targeted: {{count}} industries'), { count: (industry.length) });
};

const useStyles = makeStyles({
  hide: {
    display: 'none',
  },
});

const UnstyledIndustryPicker = (props) => {
  const {
    classes,
    updateValidation,
    validationProps,
    onAudienceTargetInfoChange,
    shouldHide,
    showExclusion,
    initialIndustryTargets,
  } = props;

  const { data: { industry, negativeIndustry } } = validationProps;

  const activity = useContext(ActivityContext);

  const [industryAssociationRef, setIndustryAssociationRef] = useState();
  const styles = useStyles();

  const currentLevelTargetInfo = useMemo(() => (
    new AudienceTargetInfo({
      resourceName: 'AdGroupCriterion',
      isForVNext: true,
      initAudienceAssociationView: false,
    })), []);

  const industryAssociationView = useMemo(() => {
    const industryView = new IndustryTargetSetting({
      criterionType: 'Industry',
      type: 'Industry',
      showBidOnlyNote: false,
      showExclusion,
      defaultBidAdjustment: 0,
      isForVNext: true,
      currentLevelTargetInfo,
    });

    industryView.render();

    return industryView;
  }, [currentLevelTargetInfo, showExclusion]);

  const onChange = () => {
    if (isEmpty(validationProps.data)) {
      // data is still loading
      return;
    }

    const updatedItems = industryAssociationView.currentLevelTargetInfo.getSnapshot(false);

    const industryTargetsBidAdjustment = [];

    const industryTargets = updatedItems.filter(({ CriterionType }) => (CriterionType === 'Industry'))
      .map(({ Id, Criterion }) => {
        const { IndustryCriterion } = Criterion;

        const targetWithBidAdjustment = {
          Id,
          Criterion,
        };

        industryTargetsBidAdjustment.push(targetWithBidAdjustment);

        return IndustryCriterion.Target;
      });

    const negativeIndustryTargets = updatedItems.filter(({ CriterionType }) => (CriterionType === 'NegativeIndustry'))
      .map(({ Criterion }) => {
        const { IndustryCriterion } = Criterion;

        return IndustryCriterion.Target;
      });

    updateValidation([], { industry: industryTargets, negativeIndustry: negativeIndustryTargets, industryTargetsBidAdjustment });

    if (activity) {
      activity.signal(`onIndustryTargetChange - ${JSON.stringify({ industry: industryTargets })}`);
    }
  };

  useEffect(() => {
    onAudienceTargetInfoChange(currentLevelTargetInfo);
  }, [currentLevelTargetInfo, onAudienceTargetInfoChange]);

  useEffect(() => {
    if (industryAssociationRef && industryAssociationRef.childElementCount === 0 && industryAssociationView) {
      industryAssociationRef.innerHTML = '';
      industryAssociationRef.append(industryAssociationView.el);
    }
  }, [industryAssociationRef, industryAssociationView]);

  useEffect(() => {
    industryAssociationView.searchView.searchTreeInitializationPromise.then(() => {
      currentLevelTargetInfo.off('AdGroupCriterionIndustryChanged', onChange);

      const industryTargets = industryAssociationView.searchView.treeView.node.children.map((child) => {
        const { children } = child;

        const innerChildren = children.map(innerChild => innerChild.data);

        return [...innerChildren];
      });

      const flatIndustryTargets = flatten(industryTargets);

      if (industry && industry.length > 0) {
        const selectedIndustries = flatIndustryTargets.filter(({ Id }) => industry.includes(Id));

        const currentTargets = currentLevelTargetInfo.getSnapshot(false).filter(({ CriterionType }) => (CriterionType === 'Industry'));

        // check if the current targets are the same as the selected targets
        const isSame = every(currentTargets, ((target) => {
          const { Criterion } = target;

          const { IndustryCriterion } = Criterion;

          return industry.includes(IndustryCriterion.Target);
        }));

        if (currentTargets.length !== selectedIndustries.length || !isSame) {
          selectedIndustries.forEach((data) => {
            const { BidAdjustment = 0, Id } = (initialIndustryTargets && initialIndustryTargets.find(({ criterion }) => criterion === data.Id)) || {};
            const industryCriterion = criterionHelper.buildLinkedInCriterion(
              'Industry',
              data,
              true,
              BidAdjustment,
              0
            );

            const criterion = {
              ...industryCriterion,
              Id,
            };

            currentLevelTargetInfo.createAsync(criterion);
          });
        }
      }

      if (negativeIndustry && negativeIndustry.length > 0) {
        const selectedNegativeIndustries = flatIndustryTargets.filter(({ Id }) => negativeIndustry.includes(Id));

        const currentNegativeTargets = currentLevelTargetInfo.getSnapshot(false).filter(({ CriterionType }) => (CriterionType === 'NegativeIndustry'));

        // check if the current targets are the same as the selected targets
        const isSame = every(currentNegativeTargets, ((target) => {
          const { Criterion } = target;

          const { IndustryCriterion } = Criterion;

          return negativeIndustry.includes(IndustryCriterion.Target);
        }));

        if (currentNegativeTargets.length !== selectedNegativeIndustries.length || !isSame) {
          selectedNegativeIndustries.forEach((data) => {
            const criterion = criterionHelper.buildLinkedInCriterion(
              'Industry',
              data,
              false,
              0,
              0
            );

            currentLevelTargetInfo.createAsync(criterion);
          });
        }
      }
    }).then(() => {
      currentLevelTargetInfo.on('AdGroupCriterionIndustryChanged', onChange);
      currentLevelTargetInfo.trigger('AdGroupCriterionIndustryChanged'); // trigger the initial validation
    });

    return () => {
      currentLevelTargetInfo.off('AdGroupCriterionIndustryChanged', onChange);
    };
  }, [industry, negativeIndustry]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classNames('industry-picker crud-audience-adgroup audience-target-settings-container', classes.pickerContainer, shouldHide && styles.hide)} >
      <div className={classNames('industry-association-view', 'criterion-setting-Industry')} ref={newRef => setIndustryAssociationRef(newRef)} />
    </div>
  );
};

UnstyledIndustryPicker.propTypes = {
  classes: PropTypes.shape({
    pickerContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      industry: PropTypes.arrayOf(PropTypes.shape([])),
      negativeIndustry: PropTypes.arrayOf(PropTypes.shape([])),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  onAudienceTargetInfoChange: PropTypes.func,
  shouldHide: PropTypes.bool,
  showExclusion: PropTypes.bool,
  initialIndustryTargets: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

UnstyledIndustryPicker.defaultProps = {
  shouldHide: false,
  showExclusion: true,
  onAudienceTargetInfoChange: noop,
};

const UnstyledIndustryPickerWithBidAdjustment = withBidAdjustment(UnstyledIndustryPicker, 'Industry', ['industry', 'negativeIndustry']);

export const ValidatedIndustryPicker = withDefaultStyles(withCustomValidation(UnstyledIndustryPickerWithBidAdjustment), getIndustryTargetStyles);
